/**
 * Ligas: configuração, membros, ciclo de vida e ações do comissário.
 *
 * Rotas em API 8. Os nomes dos campos vêm do bloco `LeagueSettings` do mesmo
 * documento e os conjuntos de valores das colunas descritas em DATABASE 2.5,
 * que é onde a liga é gravada.
 */
import { z } from "zod";
import { CountryCodeSchema, DifficultySchema, IdSchema, IsoDateTimeSchema, } from "./common.js";
import { AppliedCosmeticsSchema } from "./cosmetics.js";
import { LEAGUE_MAX_HUMANS, LEAGUE_MIN_HUMANS, LEAGUE_NAME_MAX_LENGTH, LEAGUE_NAME_MIN_LENGTH, MARKET_MIN_OFFER_RATIO_FLOOR, } from "../constants.js";
// ---------------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------------
export const LeagueVisibilitySchema = z.enum(["private", "public"]);
/**
 * Estado da liga.
 *
 * API 8 lista os quatro primeiros. `stalled` entra porque é o estado que o
 * worker marca quando uma rodada falha três vezes e precisa de intervenção
 * (SERVER 4, DATABASE 2.5), e o cliente precisa saber explicar isso.
 */
export const LeagueStateSchema = z.enum(["lobby", "running", "paused", "finished", "stalled"]);
/** Papel do membro dentro da liga. */
export const MemberRoleSchema = z.enum(["commissioner", "member"]);
/** Situação do membro. Quem sai ou é expulso deixa o clube com a IA. */
export const MemberStatusSchema = z.enum(["active", "left", "kicked"]);
/** Ritmo da liga: rodada por agendamento ou rodada ao vivo. */
export const PaceModeSchema = z.enum(["async", "live"]);
/**
 * Como os clubes são distribuídos no início.
 *
 * API 8 usa `draft` no exemplo e a mesma seção fala em sortear. O outro valor
 * fica com o nome que os documentos usam em português.
 */
export const ClubAssignmentSchema = z.enum(["sorteio", "draft"]);
// ---------------------------------------------------------------------------
// Configuração
// ---------------------------------------------------------------------------
/**
 * Agendamento das rodadas.
 *
 * `cron` só é conferido aqui pela forma de cinco campos. Quem monta o job no
 * BullMQ faz a leitura completa da expressão.
 */
export const PaceSchema = z
    .object({
    mode: PaceModeSchema,
    cron: z.string().regex(/^(\S+\s+){4}\S+$/, "Expressão cron precisa de cinco campos."),
    /** Fuso IANA, como `America/Sao_Paulo`. */
    timezone: z.string().min(1).max(64),
    /** Minutos de espera pelos ausentes antes de rodar no modo ao vivo. */
    liveTimeoutMinutes: z.number().int().min(1).max(120),
})
    .strict();
/** Regras do mercado entre humanos (SECURITY 5). */
export const LeagueMarketSettingsSchema = z
    .object({
    humanTrades: z.boolean(),
    minOfferRatio: z.number().finite().min(MARKET_MIN_OFFER_RATIO_FLOOR).max(1),
    maxTradesPerPair: z.number().int().min(0).max(10),
    commissionerApproval: z.boolean(),
})
    .strict();
/** Corpo de `POST /leagues` e bloco `settings` da liga (API 8). */
export const LeagueSettingsSchema = z
    .object({
    name: z.string().trim().min(LEAGUE_NAME_MIN_LENGTH).max(LEAGUE_NAME_MAX_LENGTH),
    visibility: LeagueVisibilitySchema,
    maxHumans: z.number().int().min(LEAGUE_MIN_HUMANS).max(LEAGUE_MAX_HUMANS),
    /** Datapack usado no mundo. `null` usa o banco padrão do motor. */
    datapackId: IdSchema.nullable(),
    countries: z.array(CountryCodeSchema).min(1).max(8),
    /** O gerador do motor monta até quatro divisões por país. */
    divisionsPerCountry: z.number().int().min(1).max(4),
    clubAssignment: ClubAssignmentSchema,
    pace: PaceSchema,
    seasons: z.number().int().min(1).max(20),
    market: LeagueMarketSettingsSchema,
    difficulty: DifficultySchema,
})
    .strict();
/** `POST /leagues`. */
export const CreateLeagueRequestSchema = LeagueSettingsSchema;
/**
 * `PATCH /leagues/:id`.
 *
 * Só o comissário. Fora do lobby, o servidor aceita apenas `pace` e `deadline`
 * (API 8), regra que fica no `service.ts`, não no esquema.
 */
export const UpdateLeagueRequestSchema = LeagueSettingsSchema.partial()
    .extend({ deadline: IsoDateTimeSchema.optional() })
    .strict();
// ---------------------------------------------------------------------------
// Membros e listagens
// ---------------------------------------------------------------------------
export const LeagueMemberSchema = z.object({
    userId: IdSchema,
    displayName: z.string().min(1),
    /** Clube que o membro comanda. `null` enquanto o sorteio não aconteceu. */
    clubId: IdSchema.nullable(),
    clubName: z.string().nullable(),
    role: MemberRoleSchema,
    status: MemberStatusSchema,
    ready: z.boolean(),
    online: z.boolean(),
    joinedAt: IsoDateTimeSchema,
    lastActionAt: IsoDateTimeSchema.nullable(),
    /**
     * Cosméticos deste membro, já conferidos contra o direito no servidor.
     *
     * Vêm resolvidos em parâmetros e não em `sku`: o cliente desenha o que
     * recebeu e não tem como vestir nada que a pessoa não comprou.
     */
    cosmetics: AppliedCosmeticsSchema,
});
/**
 * Linha de `GET /leagues/mine` e `GET /leagues/public`.
 *
 * Os campos obrigatórios são os que o exemplo de API 16 traz. Os demais são
 * opcionais porque a listagem pública precisa deles e a minha não, e assim a
 * resposta do documento continua válida.
 */
export const LeagueSummarySchema = z.object({
    id: IdSchema,
    name: z.string().min(1),
    state: LeagueStateSchema,
    round: z.number().int().min(0),
    /** Prazo da próxima rodada. `null` no lobby e no fim da liga. */
    deadline: IsoDateTimeSchema.nullable(),
    /** Meu clube na liga. `null` em liga pública que eu só observo. */
    myClubId: IdSchema.nullable(),
    visibility: LeagueVisibilitySchema.optional(),
    season: z.number().int().min(0).optional(),
    humanCount: z.number().int().min(0).optional(),
    maxHumans: z.number().int().min(0).optional(),
});
/** `GET /leagues/:id`. Aqui os campos opcionais do resumo são obrigatórios. */
export const LeagueDetailSchema = LeagueSummarySchema.extend({
    visibility: LeagueVisibilitySchema,
    season: z.number().int().min(0),
    humanCount: z.number().int().min(0),
    maxHumans: z.number().int().min(0),
    ownerId: IdSchema,
    settings: LeagueSettingsSchema,
    members: z.array(LeagueMemberSchema),
    engineVersion: z.string().min(1),
    createdAt: IsoDateTimeSchema,
    startedAt: IsoDateTimeSchema.nullable(),
    finishedAt: IsoDateTimeSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Convites e ações
// ---------------------------------------------------------------------------
/**
 * Código de convite, curto e fácil de ditar.
 *
 * Aceita hífen e espaço e joga fora antes de conferir. O código é gravado sem
 * separador, mas quem recebe copia de um print, de uma mensagem ou de alguém
 * falando ao telefone, e vai digitar `ABCD-EFGH` tanto quanto `abcdefgh`.
 * Recusar as duas formas mais prováveis de digitar o código certo é transformar
 * um convite em suporte.
 */
export const InviteCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .transform((valor) => valor.replace(/[\s-]/g, ""))
    .pipe(z.string().regex(/^[A-Z0-9]{6,12}$/, "Código de convite inválido."));
/** `POST /leagues/:id/invites`. Sem `userId`, devolve um código. */
export const CreateInviteRequestSchema = z
    .object({ userId: IdSchema.optional() })
    .strict();
export const InviteSchema = z.object({
    code: InviteCodeSchema,
    expiresAt: IsoDateTimeSchema.nullable(),
    /** Preenchido quando o convite foi dirigido a alguém. */
    userId: IdSchema.nullable(),
});
/** `POST /leagues/join`. */
export const JoinLeagueRequestSchema = z.object({ code: InviteCodeSchema }).strict();
/** `POST /leagues/:id/kick`. */
export const KickMemberRequestSchema = z.object({ userId: IdSchema }).strict();
/** `POST /leagues/:id/ready`, usado no modo ao vivo. */
export const ReadyRequestSchema = z.object({ ready: z.boolean() }).strict();
/** `POST /leagues/:id/pause` e `/resume`. */
export const PauseLeagueRequestSchema = z
    .object({ reason: z.string().trim().max(200).optional() })
    .strict();
/** `POST /leagues/:id/force-round`, com a rodada esperada para idempotência. */
export const ForceRoundRequestSchema = z
    .object({ expectedRound: z.number().int().min(0) })
    .strict();
// ---------------------------------------------------------------------------
// Auditoria
// ---------------------------------------------------------------------------
/** Entrada do log da liga (`GET /leagues/:id/log`, DATABASE 2.5). */
export const LeagueLogEntrySchema = z.object({
    id: IdSchema,
    round: z.number().int().min(0),
    /** Quem agiu. `null` quando foi o próprio worker. */
    actorUserId: IdSchema.nullable(),
    kind: z.string().min(1),
    payload: z.record(z.unknown()),
    createdAt: IsoDateTimeSchema,
});
//# sourceMappingURL=leagues.js.map